import { FRAME_RATES, type FrameRateId } from '../frame/frameRates';
import type { ThemeId } from '../state/preferences';

export interface ControlsCallbacks {
  onRateChange(id: FrameRateId): void;
  onThemeChange(theme: ThemeId): void;
  onNtpToggle(enabled: boolean): void;
  onCaptureMode(): void;
}

export interface ControlsView {
  setRate(id: FrameRateId): void;
  setTheme(theme: ThemeId): void;
  setNtpSync(enabled: boolean, error?: string): void;
}

const THEMES: readonly ThemeId[] = ['dark', 'color', 'light'];

export function createControlsView(host: HTMLElement, cb: ControlsCallbacks): ControlsView {
  host.classList.add('controls');
  host.innerHTML = `
    <div class="controls__themes" data-role="themes">
      ${THEMES.map((t) => `<button type="button" class="controls__btn" data-theme="${t}">${t}</button>`).join('')}
    </div>
    <div class="controls__rates" data-role="rates">
      ${FRAME_RATES.map((r) => `<button type="button" class="controls__btn" data-rate="${r.id}">${r.label}</button>`).join('')}
    </div>
    <div class="controls__bottom">
      <label class="controls__sync">
        <input type="checkbox" data-role="ntp" />
        <span>Sync time</span>
        <span class="controls__sync-error" data-role="ntp-error" hidden></span>
      </label>
      <button type="button" class="controls__btn controls__capture" data-role="capture">Capture mode</button>
    </div>
  `;

  host.querySelectorAll<HTMLButtonElement>('[data-theme]').forEach((btn) => {
    btn.addEventListener('click', () => cb.onThemeChange(btn.dataset.theme as ThemeId));
  });
  host.querySelectorAll<HTMLButtonElement>('[data-rate]').forEach((btn) => {
    btn.addEventListener('click', () => cb.onRateChange(btn.dataset.rate as FrameRateId));
  });
  const ntpInput = host.querySelector<HTMLInputElement>('[data-role="ntp"]')!;
  ntpInput.addEventListener('change', () => cb.onNtpToggle(ntpInput.checked));
  host.querySelector<HTMLButtonElement>('[data-role="capture"]')!
    .addEventListener('click', () => cb.onCaptureMode());

  const ntpError = host.querySelector<HTMLElement>('[data-role="ntp-error"]')!;

  return {
    setRate(id) {
      host.querySelectorAll<HTMLButtonElement>('[data-rate]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.rate === id);
      });
    },
    setTheme(theme) {
      host.querySelectorAll<HTMLButtonElement>('[data-theme]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.theme === theme);
      });
    },
    setNtpSync(enabled, error) {
      ntpInput.checked = enabled;
      if (error) {
        ntpError.textContent = error;
        ntpError.hidden = false;
      } else {
        ntpError.textContent = '';
        ntpError.hidden = true;
      }
    },
  };
}
